import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "src/components/Buttons/Button";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { toast } from "react-toastify";
import { getUserInfo, getUserRolePermissions } from 'src/services/userServicer';
import Select from 'react-select';
import { getControls } from "src/services/controls";
import { fetchAllCategoryDataApi, fetchAllDataApi } from "src/services/riskServices";
import { useForm } from "react-hook-form";
import { MdOutlineDelete } from "react-icons/md";
import { createQuestion } from "src/services/question"
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";

const CreateQuestionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notAllowed, setNotAllowed] = useState(false);
  const [userPermissions, setUserPermissions] = useState<any>(null);

  const { register, handleSubmit, formState: { errors }, watch, setValue, trigger } = useForm();
  const [question, setQuestion] = useState({
    en_questionText: '',
    ar_questionText: '',
    type: '',
    options: [] as string[],
    risk: '',
    control: '',
    category: ''
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setQuestion((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOptionChange = (index: number, value: string) => {
    const updatedOptions = [...question.options];
    updatedOptions[index] = value;
    setQuestion((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  const addOption = () => {
    setQuestion((prev) => ({
      ...prev,
      options: [...prev.options, ''],
    }));
  };

  const removeOption = (index: number) => {
    const updatedOptions = question.options.filter((_, i) => i !== index);
    setQuestion((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  const onSubmit = async(data: any) => {
    if(question.type === 'compliance_related'){
      question.options = ['Fully Implemented','Implemented','Mostly Implemented','Partially Implemented','Averagely Implemented', 'Not Implemented']
    }
    if(question.type === 'risk_related'){
      question.options = ['Least','Minimal','Moderate','Significant','Most']
    }
    const response = await createQuestion(question);
    if (response.success) {
      toast.success(response.message);
      navigate('/bankQuestions');
    } else {
      toast.error(response.message);
    }
    
  };

  const [controlsOptions, setControlsOptions] = useState<any[]>([]);
  const [riskOptions, setRiskOptions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);

  const fetchControls = async () => {
    try {
      const controls: any = await getControls();
   
      if (controls && controls.success && Array.isArray(controls.data)) {
        let controlsOptions = controls.data.map((control: any) => ({
          value: control.id,
          label: control.description
        }));
        setControlsOptions(controlsOptions);
      } else {
        console.warn("Unexpected controls response format:", controls);
        setControlsOptions([]);
      }
    } catch (error: any) {
      console.error("Error fetching controls:", error);
      setControlsOptions([]);
    }
  };

  const fetchRisks = async () => {
    try {
      const risks: any = await fetchAllDataApi();
      
      if (risks && risks.success && Array.isArray(risks.data)) {
        let risksOptions = risks.data.map((risk: any) => ({
          value: risk.id,
          label: risk?.name
        }));
        setRiskOptions(risksOptions);
      } else if (risks && Array.isArray(risks.data)) {
        let risksOptions = risks.data.map((risk: any) => ({
          value: risk.id,
          label: risk?.name
        }));
        setRiskOptions(risksOptions);
      } else {
        console.warn("Unexpected risks response format:", risks);
        setRiskOptions([]);
      }
    } catch (error: any) {
      console.error("Error fetching risks:", error);
      setRiskOptions([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchAllCategoryDataApi();
      // The API function now always returns { success: true, data: [] } on error
      // So we can safely access response.data
      if (response && response.success !== false) {
        if (Array.isArray(response.data)) {
          setCategories(response.data);
        } else if (Array.isArray(response)) {
          // Handle case where response is directly an array
          setCategories(response);
        } else {
          console.warn("Unexpected categories response format:", response);
          setCategories([]);
        }
      } else {
        setCategories([]);
      }
    } catch (error: any) {
      console.error("Error fetching categories:", error);
      // Don't show error toast for categories, just log it and set empty array
      setCategories([]);
    }
  };

  useEffect(() => {
    const fetchUserRolePermissions = async () => {
      try {
        const user = await getUserInfo();
        if (!user?.id) {
          setNotAllowed(true);
          return;
        }
        let userId =  user.id || "";
        const permissions = await getUserRolePermissions(userId);
        if (permissions.success) {
          // Check for survey permission first, fallback to compliances
          const surveyPagePermission = permissions.data.permissions.find((permission: any) => permission.page === "survey");
          const compliancePagePermission = permissions.data.permissions.find((permission: any) => permission.page === "compliances");
          const pagePermission = surveyPagePermission || compliancePagePermission;
          
          if (pagePermission) {
            setUserPermissions(pagePermission.permissions || pagePermission);
            const hasShowPermission = (pagePermission.permissions?.show ?? pagePermission.show) ?? true;
            if (hasShowPermission) {
              setNotAllowed(false);
            } else {
              setNotAllowed(true);
            }
          } else {
            // If no specific permission found, allow access (fallback behavior)
            setNotAllowed(false);
          }
        } else {
          toast.error(permissions.message || t("permissions.Error_fetching_user_role_permissions"));
          setNotAllowed(true);
        }
      } catch (error: any) {
        const errorMessage = error?.message || error?.toString() || t("permissions.Error_fetching_user_role_permissions");
        toast.error(t("permissions.Error_fetching_user_role_permissions") + ": " + errorMessage);
        setNotAllowed(true);
      }
    };

    const loadData = async () => {
      try {
        await fetchUserRolePermissions();
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
      
      // Load other data in parallel, but don't block on errors
      try {
        await Promise.allSettled([
          fetchControls(),
          fetchRisks(),
          fetchCategories()
        ]);
      } catch (error) {
        console.error("Error loading form data:", error);
      }
    };

    loadData();
  }, []);

  // Custom styles for react-select to reduce height
  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '50px',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height: '40px',
      padding: '0 6px',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: '40px',
    }),
  };

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t("header.you_are_here")}</BreadcrumbItem>
        <BreadcrumbItem>{t("question.Questions")}</BreadcrumbItem>
        <BreadcrumbItem active style={{ paddingRight: '10px' }}>
          {t("question.Create_question")}
        </BreadcrumbItem>
      </Breadcrumb>

      <h5 className="mb-lg">{t('question.Create_question')}</h5>
      {!notAllowed && <div className="container_info">
        <form onSubmit={handleSubmit(onSubmit)} className="create-compliance-form">
          <div className="form-section2 mb-5">
            <h5 className="section-title">{t('question.BasicInfo')}</h5>
            <div className="form-group">
              <label>{t("question.English Question")}</label>
              <input
                type="text"
                value={question.en_questionText}
                
                className="form-control"
                {...register("en_questionText", { required: true })}
                onChange={handleInputChange}
              />
              {errors.en_questionText && <span className="text-danger">{t('surveys.This field is required')}</span>}
            </div>
            <div className="form-group">
              <label>{t("question.Arabic Question")}</label>
              <input
                type="text"
                value={question.ar_questionText}
              
                className="form-control"
                {...register("ar_questionText", { required: true })}
                onChange={handleInputChange}
              />
              {errors.ar_questionText && <span className="text-danger">{t('surveys.This field is required')}</span>}
            </div>

            {/* Category Dropdown */}
            <div className="form-group">
              <label htmlFor="category">{t("question.Category")}</label>
              <select
                id="category"
                style={{height: '40px',padding: '0px 10px'}}
                className={`form-control ${errors.category ? "is-invalid" : ""}`}
                {...register("category", { required: t("question.Category is required") ?? "Category is required" })}
                onChange={handleInputChange}
              >
                <option value="">{t("question.Select Category")}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category?.name}
                  </option>
                ))}
              </select>
              {errors.category?.message && (
                <div className="invalid-feedback">
                  {String(errors.category.message)}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{t("question.Type")}</label>
              <Select
                options={[
                  { value: 'textarea', label: t("question.Text") },
                  { value: 'multiple_choice', label: t("question.Multiple Choice") },
                  { value: 'single_choice', label: t("question.Single Choice") },
                    { value:'compliance_related', label:t('question.Related to compliance')},
                    { value:'risk_related', label:t('question.Related to risk')}
                ]}

                
                className="basic-single"
                classNamePrefix="select"
                styles={customSelectStyles}
                onChange={(selectedOption: any) => {
                  const value = selectedOption ? selectedOption.value : '';
                  setQuestion((prev) => ({
                    ...prev,
                    type: value
                  }));
                  setValue("type", value);
                  trigger("type");
                }}
              />
              {errors.type && <span className="text-danger">{t('surveys.This field is required')}</span>}
            </div>

            {(question.type === "multiple_choice" || question.type === "single_choice" )&& (
              <div className="form-group">
                <label className="form-label">{t("question.Options")}</label>
                <button
                  type="button"
                  onClick={addOption}
                  className="btn btn-warning mr-2 ml-2"
                >
                 + {t("question.Add Option")}
                </button>
                {question.options.map((option, index) => (
                  <div key={index} className="d-flex flex-row mt-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      className="form-control"
                      placeholder={`${t("question.Option")} ${index + 1}`}
                      required
                    />
                    
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="action-button-danger"
                    >
                       <MdOutlineDelete size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-section2 mb-5" style={{display:question.type === 'compliance_related' || question.type === 'risk_related' ? 'block' : 'none'}}>
            <h5 className="section-title">{t('question.relatedTo')}</h5>

            <div className="form-group" style={{display:question.type === 'compliance_related' ? 'block' : 'none'}}>
              <label className="form-label">{t("question.Control")}</label>
              <Select
                options={controlsOptions}
                isMulti={true}
                className="basic-single"
                classNamePrefix="select"
                styles={customSelectStyles}
                {...register("control")}
                onChange={(selectedOptions: any) => {
                  setQuestion((prev) => ({
                    ...prev,
                    control: selectedOptions ? selectedOptions.map((option: any) => option.value) : []
                  }));
                  setValue("control", selectedOptions ? selectedOptions.map((option: any) => option.value) : []);
                  trigger("control");
                }}
              />
            </div>

            <div className="form-group" style={{display:question.type === 'risk_related' ? 'block' : 'none'}}>
              <label className="form-label">{t("question.Risk")}</label>
              <Select
                options={riskOptions}
                isMulti={true}
                className="basic-single"
                classNamePrefix="select"
                styles={customSelectStyles}
                {...register("risk")}
                onChange={(selectedOptions: any) => {
                  setQuestion((prev) => ({
                    ...prev,
                    risk: selectedOptions ? selectedOptions.map((option: any) => option.value) : []
                  }));
                  setValue("risk", selectedOptions ? selectedOptions.map((option: any) => option.value) : []);
                  trigger("risk");
                }}
              />
            </div>
            
          </div>

          <button type="submit" className="submit-button4">
              {t("question.Create Question")}
            </button>
        </form>
      </div>}

      {notAllowed && <div className="container_info">
        <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
          <h5 style={{ width: '100%' }} className="text-center">{t("permissions.not_allowed")}</h5>
        </div>
      </div>}
    </div>
  );
};

export default CreateQuestionPage;